import type { UserRole } from "@/features/settings/api";

export const ROLE_LABELS: Record<UserRole, string> = {
  OWNER: "Chủ tài khoản",
  MANAGER: "Quản lý",
  AGENT: "Đại diện",
  SOURCE: "Nguồn",
  VIEWER: "Người xem",
};

export function labelForRole(role: string): string {
  return ROLE_LABELS[role as UserRole] ?? role;
}

/** Roles that may grant/edit which other roles. Mirrors the backend rule. */
export function canAssignRole(actor: UserRole, target: UserRole): boolean {
  if (actor === "OWNER") return true;
  if (actor === "MANAGER") {
    // Managers can assign Manager/Agent/Viewer; never Owner.
    return target !== "OWNER";
  }
  return false;
}

/** True when the actor's role grants access to the Team tab. */
export function canManageTeam(role: string | undefined): boolean {
  return role === "OWNER" || role === "MANAGER";
}

/**
 * Roles that may inherit a deleted user's book, given that user's role. The
 * target must be able to own what that user owned: a SOURCE's properties → an
 * OWNER or another SOURCE; an AGENT's leads/customers → an OWNER or another
 * AGENT; anyone else (MANAGER/OWNER may hold a mixed book, VIEWER holds nothing)
 * → an OWNER only. Mirrors the backend `UserService.allowedTargetRoles`.
 */
export function allowedReassignTargetRoles(deletedRole: UserRole): UserRole[] {
  if (deletedRole === "SOURCE") return ["OWNER", "SOURCE"];
  if (deletedRole === "AGENT") return ["OWNER", "AGENT"];
  return ["OWNER"];
}

/** True when the actor's role grants edit on the Organization tab. */
export function canEditOrganization(role: string | undefined): boolean {
  return role === "OWNER";
}

export function canCreateProperty(role: string | undefined): boolean {
  return role === "OWNER" || role === "MANAGER" || role === "SOURCE";
}

/**
 * OWNER/MANAGER see every lead and may assign leads to any broker; agents see
 * and own only their own leads. Mirrors the backend `LeadAccess` rule, so the
 * UI can hide broker filters / assignment pickers that would do nothing (or
 * mislead) for an agent.
 */
export function seesAllLeads(role: string | undefined): boolean {
  return role === "OWNER" || role === "MANAGER";
}

/**
 * OWNER/MANAGER see every viewing and may filter by broker; agents see only
 * viewings they're a party to. Mirrors the backend appointment scoping, so the
 * UI can hide the broker filter that would do nothing for an agent.
 */
export function seesAllAppointments(role: string | undefined): boolean {
  return role === "OWNER" || role === "MANAGER";
}
